/**
 * HoldedInvoicesModal - Modal para mostrar facturas pendientes de generar en Holded
 *
 * Muestra una lista de facturas que aún no tienen erp_id (no están en Holded)
 * y permite confirmar la generación de estas facturas.
 */

import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { Invoice } from '../../models/Invoices';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

export type HoldedModalMode = 'generate' | 'confirm';

export interface HoldedInvoicesModalRef {
  open: (invoices: Invoice[], onConfirm: () => void, mode?: HoldedModalMode) => void;
}

interface HoldedInvoicesModalProps {
  onConfirm?: () => void;
}

export const HoldedInvoicesModal = forwardRef<HoldedInvoicesModalRef, HoldedInvoicesModalProps>(
  ({ onConfirm }, ref) => {
    const { t } = useTranslation('invoicesModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [onConfirmCallback, setOnConfirmCallback] = useState<(() => void) | null>(null);
    const [mode, setMode] = useState<HoldedModalMode>('generate');
    const currency = useCurrency();

    useImperativeHandle(ref, () => ({
      open: (invoicesList: Invoice[], confirmCallback: () => void, modalMode: HoldedModalMode = 'generate') => {
        setInvoices(invoicesList);
        setOnConfirmCallback(() => confirmCallback);
        setMode(modalMode);
        modalRef.current?.openModal();
      },
    }));

    const handleConfirm = () => {
      if (onConfirmCallback) {
        onConfirmCallback();
      }
      onConfirm?.();
      modalRef.current?.closeModal();
      setInvoices([]);
      setOnConfirmCallback(null);
    };

    const handleCancel = () => {
      modalRef.current?.closeModal();
      setInvoices([]);
      setOnConfirmCallback(null);
    };

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    };

    const isConfirmMode = mode === 'confirm';
    const titleKey = isConfirmMode ? 'modals.holded.confirmPending.title' : 'modals.holded.pending.title';
    const descriptionKey = isConfirmMode
      ? 'modals.holded.confirmPending.description'
      : 'modals.holded.pending.description';
    const actionTitleKey = isConfirmMode
      ? 'modals.holded.confirmPending.actionTitle'
      : 'modals.holded.pending.actionTitle';
    const action1Key = isConfirmMode ? 'modals.holded.confirmPending.action1' : 'modals.holded.pending.action1';
    const action2Key = isConfirmMode ? 'modals.holded.confirmPending.action2' : 'modals.holded.pending.action2';
    const confirmButtonKey = isConfirmMode ? 'modals.holded.confirm.confirm' : 'modals.holded.generate.confirm';

    return (
      <ModalCenter
        ref={modalRef}
        title={t(
          titleKey,
          isConfirmMode ? 'Facturas pendientes de confirmar en Holded' : 'Facturas pendientes de generar en Holded',
        )}
        width="xl"
      >
        <div className="space-y-4">
          {/* Descripción */}
          <div className="space-y-2">
            <p className="text-sm text-complementary-text">
              {t(
                descriptionKey,
                isConfirmMode
                  ? 'Se encontraron {{count}} factura(s) pendiente(s) de confirmar en Holded. ¿Deseas continuar con la confirmación?'
                  : 'Se encontraron {{count}} factura(s) pendiente(s) de generar en Holded. ¿Deseas continuar con la generación?',
                { count: invoices.length },
              )}
            </p>
            <div className="text-sm text-complementary-text">
              <p className="mb-1">{t(actionTitleKey, 'Esta acción:')}</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  {t(
                    action1Key,
                    isConfirmMode
                      ? 'Actualizará los borradores desde Holded'
                      : 'Buscará facturas sin generar en Holded',
                  )}
                </li>
                <li>
                  {t(
                    action2Key,
                    isConfirmMode
                      ? 'Generará los PDFs definitivos y procesará los movimientos en las billeteras de los clientes'
                      : 'Creará los documentos en Holded',
                  )}
                </li>
              </ul>
            </div>
          </div>

          {/* Tabla de facturas */}
          {invoices.length > 0 && (
            <div className="border border-card-border rounded-lg overflow-hidden">
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('table.erp_number', 'Factura')}</TableHead>
                      <TableHead>{t('table.date', 'Fecha')}</TableHead>
                      <TableHead>{t('table.period', 'Período')}</TableHead>
                      <TableHead>{t('filters.teamId', 'Equipo')}</TableHead>
                      <TableHead className="text-right">{t('filters.baseTotal', 'Subtotal')}</TableHead>
                      <TableHead className="text-right">{t('filters.taxesTotal', 'Impuestos')}</TableHead>
                      <TableHead className="text-right">{t('table.grand_total', 'Total')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.erp_number || `#${invoice.id}`}</TableCell>
                        <TableCell>{invoice.date ? formatDate(invoice.date) : '-'}</TableCell>
                        <TableCell>{invoice.period_month || '-'}</TableCell>
                        <TableCell>{invoice.team?.name || '-'}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(invoice.base_total)} {currency}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(invoice.taxes_total)} {currency}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(invoice.grand_total)} {currency}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex justify-end gap-3 pt-4 border-t border-card-border">
            <Button variant="outline" onClick={handleCancel}>
              {t('modals.holded.generate.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleConfirm} disabled={invoices.length === 0}>
              {t(confirmButtonKey, isConfirmMode ? 'Sí, confirmar' : 'Sí, generar')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

HoldedInvoicesModal.displayName = 'HoldedInvoicesModal';
